import React, { useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { SystemAlarmsModal } from './SystemAlarmsModal';
import { Device } from '../features/device/types/device';

interface Alarm {
    id: number;
    name: string;
    active: boolean;
}

interface ApiAlarm {
    id: number;
    name: string;
    alarm_text: string;
    trigger_tag: string;
    trigger_bit: number;
    info_text: string;
    edge_uid: string;
    alarmWord: number;
}

interface SystemAlarmsProps {
    apiResponse?: any;
    edgeUid?: string;
    device?: Device;
}

export const SystemAlarms: React.FC<SystemAlarmsProps> = ({
    apiResponse,
    edgeUid,
    device,
}) => {
    const [isAlarmsModalOpen, setIsAlarmsModalOpen] = useState(false);
    const isRaptor =
        (typeof apiResponse?.coatingType === 'string' && apiResponse.coatingType.toLowerCase() === 'raptor') ||
        !!apiResponse?.deviceConfiguration;

    const getDisplayAlarms = (): Alarm[] => {
        if (apiResponse?.matchedAlarmList && Array.isArray(apiResponse.matchedAlarmList)) {
            return apiResponse.matchedAlarmList.map((alarm: ApiAlarm) => ({
                id: alarm.id,
                name: alarm.alarm_text || alarm.name || alarm.info_text,
                active: true,
            }));
        }
        return [];
    };

    const displayAlarms = getDisplayAlarms();
    const activeAlarmCount = displayAlarms.filter(a => a.active).length;

    return (
        <>
            <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Bell className="h-5 w-5 text-red-500" />
                        System Alarms
                        <span className="text-sm font-normal text-gray-500">({activeAlarmCount} active)</span>
                    </h3>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!edgeUid}
                        onClick={() => setIsAlarmsModalOpen(true)}
                    >
                        View All
                    </Button>
                </div>
                <div className="p-6">
                    {displayAlarms.length === 0 ? (
                        <div className="text-center py-8 text-green-600">
                            ✅ No active alarms
                        </div>
                    ) : (
                        <div className={`space-y-2 overflow-auto ${isRaptor ? 'max-h-80' : 'max-h-48'}`}>
                            {displayAlarms.map((alarm) => (
                                <div
                                    key={alarm.id}
                                    className={`flex items-center justify-between p-3 rounded-lg ${
                                        alarm.active
                                            ? 'bg-red-50 border'
                                            : 'bg-gray-50 border border-gray-200'
                                    }`}
                                >
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-900">{alarm.name}</span>
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        alarm.active
                                            ? 'text-readable-red bg-[rgba(196,0,31,0.06)]'
                                            : 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                                    }`}>
                                        {alarm.active ? '🔴 Active' : '✅ Normal'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <SystemAlarmsModal
                isOpen={isAlarmsModalOpen}
                onClose={() => setIsAlarmsModalOpen(false)}
                edgeUid={edgeUid}
                device={device}
            />
        </>
    );
};
